import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { Clock, Hash, MessageSquare, Users } from "lucide-react";

import { useEffect, useMemo, useRef } from "react";
import { toHTML } from "slack-markdown";

import { Badge } from "@/components/ui/badge";
import type {
  Message,
  MicrosoftTeamsMessageAttachment,
} from "@cohesive/api-entities";

import { ExpandableContainer } from "../../ExpandableContainer";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardTitle,
  ContextMenuAction,
  Process,
} from "../BaseCard";
import "./MessageCardCustomElements";

export { Message };

type MessageCardProps = Message & {
  onClick?: () => void;
  onDoubleClick?: () => void;
  onMentionClick?: () => void;
  platform: string;
  attachments?: MicrosoftTeamsMessageAttachment[];
  actions?: ContextMenuAction[];
  processes?: Process[];
  className?: string;
  style?: React.CSSProperties;
};

const formatUser = ({ id, name }: { id: string; name: string }) => {
  if (!name) {
    return `<strong>@${id}</strong>`;
  }

  return `<strong>@${name}</strong>`;
};

const formatChannel = ({ id, name }: { id: string; name: string }) => {
  if (!name) {
    return `<strong>#${id}</strong>`;
  }

  return `<strong>#${name}</strong>`;
};

const userGroup = ({ id, name }: { id: string; name: string }) => {
  if (!name) {
    return `<strong>@${id}</strong>`;
  }

  return `<strong>@${name}</strong>`;
};

// Avatar component for MessageCard
export function MessageCardAvatar({
  user_image_url,
  user_name,
  className,
  style,
}: Partial<MessageCardProps>) {
  return (
    <div
      className={cn(
        "h-10 w-10 shrink-0 flex items-center justify-center bg-muted rounded-full overflow-hidden",
        className,
      )}
      style={style}
    >
      {user_image_url ? (
        <>
          <img
            src={user_image_url}
            alt={user_name || "User"}
            className="h-full w-full object-cover"
          />
          <MessageSquare
            className="h-5 w-5 text-muted-foreground"
            style={{ display: "none" }}
          />
        </>
      ) : (
        <MessageSquare className="h-5 w-5 text-muted-foreground" />
      )}
    </div>
  );
}

// Microsoft Teams custom elements to x-* custom elements helper
const MS_TEAMS_CUSTOM_ELEMENTS = [
  "attachment",
  "emoji",
  "customemoji",
  "at",
  "systemEventMessage",
  "codeblock",
];

function changeTag(node: Element, tag: string) {
  const clone = createElement(tag);
  for (const attr of node.attributes) {
    clone.setAttributeNS(null, attr.name, attr.value);
  }
  while (node.firstChild) {
    clone.appendChild(node.firstChild);
  }
  node.replaceWith(clone);
  return clone;
}

function createElement(tag: string) {
  if (tag === "svg") {
    return document.createElementNS("http://www.w3.org/2000/svg", "svg");
  } else {
    return document.createElementNS("http://www.w3.org/1999/xhtml", tag);
  }
}

export function MessageCard({
  platform,
  content = "",
  content_type = "text",
  user_name,
  user_image_url,
  channel_name,
  team_name,
  timestamp,
  unix_timestamp,
  web_link,
  attachments,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: MessageCardProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      const els = Array.from(
        containerRef.current.querySelectorAll(
          MS_TEAMS_CUSTOM_ELEMENTS.join(","),
        ),
      ) as HTMLElement[];

      for (const element of Array.from<Element>(els)) {
        if (element.tagName.toLowerCase() === "attachment") {
          const id = element.getAttribute("id");
          const attachment = attachments?.find((a) => a.id === id);

          if (attachment) {
            try {
              element.setAttribute(
                "data-attachment",
                JSON.stringify(attachment),
              );
            } catch (e) {
              console.error("Error parsing attachment content", e);
            }
          }
        }

        changeTag(element, `x-${element.tagName.toLowerCase()}`);
      }
    }
  });

  const htmlText = useMemo(() => {
    if (content_type === "html") {
      return content;
    }

    return toHTML(content, {
      slackCallbacks: {
        user: formatUser,
        channel: formatChannel,
        usergroup: userGroup,
        atHere: () => `<strong>@here</strong>`,
        atChannel: () => `<strong>@channel</strong>`,
        atEveryone: () => `<strong>@everyone</strong>`,
      },
      hrefTarget: "_blank",
    });
  }, [content]);

  // Format timestamp if available
  const formattedTime = timestamp
    ? formatDistanceToNow(new Date(timestamp), { addSuffix: true })
    : unix_timestamp
      ? formatDistanceToNow(new Date(unix_timestamp * 1000), {
          addSuffix: true,
        })
      : "";

  return (
    <div className="w-full" ref={containerRef}>
      <BaseEntityCard
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        processes={processes}
        className={className}
        style={style}
      >
        <BaseEntityCardIcon className="p-0 overflow-hidden">
          <MessageCardAvatar
            user_image_url={user_image_url}
            user_name={user_name}
            className="h-full w-full"
          />
        </BaseEntityCardIcon>

        <BaseEntityCardContent>
          <BaseEntityCardHeader>
            <div className="flex flex-wrap items-center gap-2 min-w-0 flex-1">
              <BaseEntityCardTitle platform={platform}>
                {user_name || "Unknown User"}
              </BaseEntityCardTitle>
              {team_name && (
                <Badge variant="outline" className="pl-2">
                  <Users className="h-3 w-3 mr-1" />
                  {team_name}
                </Badge>
              )}
              {channel_name && channel_name !== user_name && (
                <Badge variant="outline" className="pl-2">
                  <Hash className="h-3 w-3 mr-1" />
                  {channel_name}
                </Badge>
              )}
            </div>
            <BaseEntityCardActions>
              {formattedTime && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center text-xs text-muted-foreground">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>{formattedTime}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {timestamp
                          ? new Date(timestamp).toLocaleString()
                          : unix_timestamp
                            ? new Date(unix_timestamp * 1000).toLocaleString()
                            : ""}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {web_link && (
                <BaseEntityCardExternalLinkAction
                  href={web_link}
                  tooltip={`View in ${platform}`}
                />
              )}
              {onMentionClick && (
                <BaseEntityCardMentionAction onClick={onMentionClick} />
              )}
              {actions && actions.length > 0 && (
                <BaseEntityCardContextMenu actions={actions} />
              )}
            </BaseEntityCardActions>
          </BaseEntityCardHeader>

          <BaseEntityCardBody>
            <ExpandableContainer
              maxHeight={30}
              contentClassName="text-sm text-foreground"
              buttonClassName="border-0 py-1 px-2 mt-1 h-auto bg-transparent hover:bg-muted/50"
              expandText="View Full Message"
              collapseText="Collapse Message"
            >
              <p
                className="w-full inline-code [&>pre]:whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: htmlText }}
                style={{ wordBreak: "break-word" }}
              />
            </ExpandableContainer>
          </BaseEntityCardBody>
        </BaseEntityCardContent>
      </BaseEntityCard>
    </div>
  );
}

export function MessageCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <Card
      className={cn("w-full transition-colors h-[90px]", className)}
      style={style}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-1/3 mt-1.5 mb-1.5" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-4 w-5/6 mb-1.5" />
            <Skeleton className="h-4 w-4/6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
